// Copyright (c) Docugami, Inc. All rights reserved.

import { DgXmlNames } from "./DgXmlNames";
import { DOMParser } from '@xmldom/xmldom'
import { TokenRange } from '../../src/networking/api'
import { Logger, LoggerCategory } from "./Logger";
import { TokensTextParser } from "./TokensTextParser";

const Ltxml = require('../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const XElement = Ltxml.XElement;
const XAttribute = Ltxml.XAttribute;
const XText = Ltxml.XText;
const XName = Ltxml.XName;

export const StructClassAttr = "struct_class";
export const StructureAttr = "structure";
export const StructClassListMaker = "list_marker";
export const StructClassListText = "list_text";
export const StructClassListItem = "list_item";
export const StructClassHeader = "header";
export const StructClassUser = "user_structure";
export const StructureListItemMarkerTag = "lim";
export const StructureListItemTag = "li";

export type DgmlVersion = DgmlVersionSchema1 | DgmlVersionSchema2 | DgmlVersionSchema3 | DgmlVersionSchema4;

interface DgmlVersionSchema1 {
    value: string;
    schemaVersion: number;
    eventStreamVersion: number;
    annotationStreamVersion: number;
    textEventStreamVersion: number;
    feedbackEventStreamVersion: number;
    addedChunksVersion?: string;
}

export interface DgmlVersionSchema2 {
    value: string;
    schemaVersion: number;
    eventStreamVersion: number;
    textEventStreamVersion: number;
    feedbackEventStreamVersion: number;
    annotationEventId: string;
    addedChunksVersion?: string;
}

export interface DgmlVersionSchema3 {
    value: string;
    schemaVersion: number;
    textEventStreamVersion: number;
    annotationEventId: string;
    addedChunksVersion?: string;
}

export interface DgmlVersionSchema4 {
    value: string;
    schemaVersion: number;
    textEventStreamVersion: number;
    annotationEventId: string;
}

export const parseDgmlVersion = (version: string): DgmlVersion => {
    const parts = version.split('.');
    const schemaVersion = Number.parseInt(parts[0]);

    let schema: DgmlVersion;
    if (schemaVersion === 1) {
        schema = {
            value: version,
            schemaVersion: schemaVersion,
            textEventStreamVersion: Number.parseInt(parts[1]),
            eventStreamVersion: Number.parseInt(parts[2]),
            feedbackEventStreamVersion: Number.parseInt(parts[3]),
            annotationStreamVersion: Number.parseInt(parts[4]),
            addedChunksVersion: parts[5]
        }
    }
    else if (schemaVersion === 2) {
        schema = {
            value: version,
            schemaVersion: schemaVersion,
            textEventStreamVersion: Number.parseInt(parts[1]),
            eventStreamVersion: Number.parseInt(parts[2]),
            feedbackEventStreamVersion: Number.parseInt(parts[3]),
            annotationEventId: parts[4],
            addedChunksVersion: parts[5]
        }
    }
    else if (schemaVersion === 3) {
        schema = {
            value: version,
            schemaVersion: schemaVersion,
            textEventStreamVersion: Number.parseInt(parts[1]),
            annotationEventId: parts[2],
            addedChunksVersion: parts[3]
        }
    }
    else if (schemaVersion === 4) {
        schema = {
            value: version,
            schemaVersion: schemaVersion,
            textEventStreamVersion: Number.parseInt(parts[1]),
            annotationEventId: parts[2]
        }
    }
    else {
        throw new Error("Unknown DGML schema version")
    }

    return schema;
}

interface StructuralAttrs {
    structClass?: string;
    structure?: string;
    style?: string;
    model?: string;
    id?: string;
    subTreeId?: string;
    subTreeParId?: string;
}

export class DgmlDom {
    private _dgmlRootElement: any;
    private _version: DgmlVersion | null | undefined;

    public constructor(dgml: string) {
        Ltxml.setCachedAttributeNames([DgXmlNames.spanId, DgXmlNames.fromTok, DgXmlNames.toTok]);
        this._dgmlRootElement = XElement.parse(dgml);
    }

    public get root() {
        return this._dgmlRootElement;
    }

    public get version(): DgmlVersion | null {
        if (this._version !== undefined) {
            return this._version;
        }

        const version = this.root.attribute(DgXmlNames.version)?.value;
        this._version = null;
        if (!!version) {
            this._version = parseDgmlVersion(version);
        }

        return this._version;
    }

    public removeAllTextNodes() {
        this._dgmlRootElement
            .descendantNodes()
            .where((ch: any) => 'Text' === ch.nodeType || 'Entity' === ch.nodeType)
            .forEach((node: any) => node.remove());
    }

    public static nodeContainsTokens(node: any, fromTok: number, toTok: number): boolean {
        const thisNodeFromTok = parseInt(node.attribute(DgXmlNames.fromTok).value);
        const thisNodeToTok = parseInt(node.attribute(DgXmlNames.toTok).value);
        return fromTok >= thisNodeFromTok && toTok <= thisNodeToTok;
    }

    public getSmallestContainerNode(fromTok: number, toTok: number, structure?: StructuralAttrs): any {
        if (fromTok === null || toTok === null) {
            throw new Error("nodeContainsTokens: Invalid arguments");
        }

        if (!DgmlDom.nodeContainsTokens(this._dgmlRootElement, fromTok, toTok)) {
            throw new Error(`No element found that contains the specified range ${fromTok}-${toTok}`);
        }

        let currentSmallestContainerNode = this._dgmlRootElement;
        while (true) {
            let foundChildElement: any = null;
            const children = currentSmallestContainerNode.nodesArray;
            for (let i = children.length - 1; i >= 0; --i) {
                const child = children[i];
                if (DgmlDom.isElement(child)) {
                    const tokenRange = DgmlDom.getTokenRange(child);
                    if (!!tokenRange && fromTok >= tokenRange.fromTok && toTok <= tokenRange.toTok) {
                        if (fromTok === tokenRange.fromTok && toTok === tokenRange.toTok) {
                            // Resolve using structure
                            if (!!structure?.subTreeId && child.attribute("subtreeParId")?.value === structure.subTreeId) {
                                // This element should be a child of the current node so we should return the previous smallest container node
                                break;
                            }
                        }

                        foundChildElement = child;
                        break;
                    }
                }
            }

            if (foundChildElement === null) {
                return currentSmallestContainerNode;
            }

            currentSmallestContainerNode = foundChildElement;
        }
    }

    public getOrCreateRichTextNodeBySpanId(spanId: string, fromTok: number, toTok: number, tokens?: TokensTextParser): any {
        let node = DgmlDom.getNodeBySpanId(spanId, this._dgmlRootElement);
        if (!node) {
            node = this.getSmallestContainerNode(fromTok, toTok);
        }

        // Step 1: Create a new DOM w. the contents of this node since we
        //         don't want to modify the original DOM and set elements
        //         core properties as we'd expect them to be
        const destDom = new DgmlDom(node.toString());

        destDom.root.attribute(DgXmlNames.fromTok).value = fromTok;
        destDom.root.attribute(DgXmlNames.toTok).value = toTok;
        destDom.root.attribute(DgXmlNames.spanId).value = spanId;

        // Step 2: Remove text (we'll add it back later)
        destDom.removeAllTextNodes();

        // Step 3: Remove all nodes that aren't enclosed in the range.
        destDom.removeAllDescendantsNotFullyInRange(fromTok, toTok);

        // Step 4: Remove all non-structural
        destDom.removeAllNonStructuralDescendants();

        // Step 5: [Optional] add text back
        if (!!tokens) {
            DgmlDom.detoxNode(destDom.root, tokens, true)
        }

        // Step 6: Transform to XHMTL
        destDom.transformStructuralNodes();

        return destDom.root;
    }

    private remove(node: any): void {
        if (!node?.parent) {
            Logger.LogError(LoggerCategory.Dgml, "Attempted to remove a node without a parent.");
            return;
        }

        const nodeIndex = node.parent.nodesArray.indexOf(node);
        if (nodeIndex !== -1) {
            for (const child of node.nodesArray) {
                child.parent = node.parent;
            };

            node.parent.nodesArray.splice(nodeIndex, 1, ...node.nodesArray);
        }
        else {
            Logger.LogError(LoggerCategory.Dgml, "Attempted to remove a node that is not contained in its parent's children.");
        }
    }

    public static getHtmlForNode(serializedNode: string, preserveText?: boolean): string {
        const destDom = new DgmlDom(serializedNode);

        // Remove non-structure nodes while preserving text if necessary
        destDom.removeAllNonStructuralDescendants(preserveText);

        // Transform to XHTML preserving text if necessary
        destDom.transformStructuralNodes(preserveText);

        return destDom.root?.toString() ?? '';
    }

    public static getNodeBySpanId(spanId: string, dgmlRoot: any): any {
        let node = dgmlRoot;
        while (!!node) {
            if (node.name === DgXmlNames.chunk && node.attribute(DgXmlNames.spanId)?.value === spanId) {
                // Found our target node
                return node;
            }

            const validChild = DgmlDom.getNextChildElement(node, 0);
            if (!!validChild) {
                // This node has children, so check them
                node = validChild;
            }
            else {
                while (!!node) {
                    if (node === dgmlRoot) {
                        node = null;
                        break;
                    }

                    const nextSibling = DgmlDom.getNextSiblingElement(node);
                    if (!!nextSibling) {
                        node = nextSibling;
                        break;
                    }

                    node = node.parent;
                }
            }
        }

        return null;
    }

    public static getTokenRange(node: any): TokenRange | null {
        const fromTokAttr = node?.attribute(DgXmlNames.fromTok)?.value;
        const toTokAttr = node?.attribute(DgXmlNames.toTok)?.value;
        if (!!fromTokAttr && !!toTokAttr) {
            return {
                fromTok: parseInt(fromTokAttr),
                toTok: parseInt(toTokAttr)
            }
        }

        return null;
    }

    public static isElement(node: any): boolean {
        return node.nodeType === 'Element';
    }

    public static isText(node: any): boolean {
        return node.nodeType === 'Text';
    }

    public static getNextChildElement(node: any, start: number): any {
        if (node?.nodesArray?.length > 0) {
            for (let i = start; i < node.nodesArray.length; ++i) {
                const child = node.nodesArray[i];
                if (DgmlDom.isElement(child)) {
                    return child;
                }
            }
        }

        return null;
    }

    public static getPreviousChildElement(node: any, start: number): any {
        if (node?.nodesArray?.length > 0) {
            for (let i = start; i >= 0; --i) {
                const child = node.nodesArray[i];
                if (DgmlDom.isElement(child)) {
                    return child;
                }
            }
        }

        return null;
    }

    public static getNextSiblingElement(node: any) {
        // Unfortunately there is no way to get the index of the current node and other helpers like nodesAfterSelf
        // return an Enumerable that has to allocate an array to be iterable like normal.
        if (node?.parent?.nodesArray?.length > 0) {
            const indexOfSelf = node.parent.nodesArray.indexOf(node);
            if (indexOfSelf !== -1) {
                return DgmlDom.getNextChildElement(node.parent, indexOfSelf + 1);
            }
        }

        return null;
    }

    public static getPreviousSiblingElement(node: any) {
        // Unfortunately there is no way to get the index of the current node and other helpers like nodesAfterSelf
        // return an Enumerable that has to allocate an array to be iterable like normal.
        if (node?.parent?.nodesArray?.length > 0) {
            const indexOfSelf = node.parent.nodesArray.indexOf(node);
            if (indexOfSelf !== -1) {
                return DgmlDom.getPreviousChildElement(node.parent, indexOfSelf - 1);
            }
        }

        return null;
    }

    public static getNextElementInDFS(node: any): any {
        while (!!node) {
            const validChild = DgmlDom.getNextChildElement(node, 0);
            if (!!validChild) {
                return validChild;
            }
            else {
                while (!!node) {
                    const nextSibling = DgmlDom.getNextSiblingElement(node);
                    if (!!nextSibling) {
                        return nextSibling;
                    }

                    node = node.parent;
                }
            }
        }

        return null;
    }

    private removeAllDescendantsNotFullyInRange(fromTok: number, toTok: number) {
        let iter = DgmlDom.getNextElementInDFS(this.root);
        while (iter != null) {
            const cur = iter;
            iter = DgmlDom.getNextElementInDFS(iter);

            const childFromTok = parseInt(cur.attribute(DgXmlNames.fromTok).value);
            const childToTok = parseInt(cur.attribute(DgXmlNames.toTok).value);

            if (childFromTok < fromTok || childToTok > toTok) {
                this.remove(cur);
            }
        }
    }

    private removeAllNonStructuralDescendants(preserveText?: boolean) {
        let iter = DgmlDom.getNextElementInDFS(this.root);
        while (iter != null) {
            const cur = iter;
            iter = DgmlDom.getNextElementInDFS(iter);

            const preserveNode = !!preserveText && cur.nodeType === 'Text';
            if (!preserveNode && !cur.attribute(StructureAttr)) {
                this.remove(cur);
            }
        }
    }

    private transformStructuralNodes(preserveText?: boolean) {
        const elementsWithListStyles: any[] = [];
        let iter = this.root;
        while (iter != null) {
            const cur = iter;
            iter = DgmlDom.getNextElementInDFS(iter);

            let xhtmlElem;
            if (DgmlDom.isText(cur)) {
                if (!preserveText) {
                    continue;
                }
                else {
                    xhtmlElem = cur;
                }
            }
            else if (!!cur.attribute) {
                const structAttr = cur.attribute(StructureAttr);
                if (!!structAttr) {
                    const structParts = structAttr.value.split("|")[0].split(" ");
                    let elemName = structParts[0];
                    if (elemName === "lim") {
                        elemName = "span";
                    }

                    xhtmlElem = new XElement(new XName(elemName));

                    let i = 1;
                    while (i < structParts.length - 1) {
                        const attr = new XAttribute(structParts[i], structParts[i + 1]);
                        xhtmlElem.add(attr);
                        i += 2;
                    }

                    const style = cur.attribute("style")?.value;
                    if (!!style) {
                        const attr = new XAttribute("style", style);
                        xhtmlElem.add(attr);
                    }

                    if (structAttr.value.startsWith("ol") || structAttr.value.startsWith("ul")) {
                        if (style?.substr("list-style-type:")) {
                            elementsWithListStyles.unshift(xhtmlElem);
                        }
                    }
                    else if (structAttr.value.startsWith("lim")) {
                        const list = cur.parent?.parent;
                        if (!!list && -1 !== elementsWithListStyles.indexOf(list)) {
                            cur.nodesArray = [];
                            iter = DgmlDom.getNextElementInDFS(cur);

                            const curIndex = cur.parent.nodesArray.indexOf(cur);
                            cur.parent.nodesArray.splice(curIndex, 1);
                            continue;
                        }
                    }
                }
                else {
                    xhtmlElem = new XElement(DgXmlNames.div);
                }
            }
            else {
                continue;
            }

            if (xhtmlElem !== cur) {
                for (const child of cur.nodesArray) {
                    child.parent = xhtmlElem;
                };

                xhtmlElem.parent = cur.parent;
                xhtmlElem.nodesArray = cur.nodesArray;
                if (cur === this.root) {
                    this._dgmlRootElement = xhtmlElem;
                }
                else {
                    const nodeIndex = cur.parent.nodesArray.indexOf(cur);
                    cur.parent.nodesArray.splice(nodeIndex, 1, xhtmlElem);
                }
            }
        }
    }

    private static detoxNode(node: any, tokens: TokensTextParser, compressSpaces: boolean): number {
        const origFromTok = parseInt(node.attribute(DgXmlNames.fromTok).value);
        const origToTok = parseInt(node.attribute(DgXmlNames.toTok).value);
        let lastToTok = origFromTok;

        let childIdx = 0;
        while (childIdx < node.nodesArray.length) {
            const child = node.nodesArray[childIdx++];

            if (!child.attribute) {
                continue;
            }

            const childFromTok = parseInt(child.attribute(DgXmlNames.fromTok).value);
            const childToTok = parseInt(child.attribute(DgXmlNames.toTok).value);

            if (childFromTok > lastToTok) {
                const text_array = tokens.getTextForTokenRange({ fromTok: lastToTok, toTok: childFromTok }, compressSpaces);
                const textNode = new XText(text_array.join(" "));
                textNode.parent = node;
                node.nodesArray.splice(childIdx - 1, 0, textNode);
                lastToTok = childToTok;
                childIdx += 1;
            }

            lastToTok = DgmlDom.detoxNode(child, tokens, compressSpaces);
        }

        if (lastToTok < origToTok) {
            const text_array = tokens.getTextForTokenRange({ fromTok: lastToTok, toTok: origToTok }, compressSpaces);
            const textNode = new XText(text_array.join(" "));
            node.nodesArray.splice(node.nodesArray.length, 0, textNode);
            lastToTok = origToTok;
        }

        return lastToTok;
    }
}
